import { DateTime } from "luxon";
import { MyMatch, PastConsult, RecentConsult } from "./_types";

/**
 * @name isTherapyOrPsychiatryAssessmentAppointmentType
 * @description Booking/event service types are either a consult ("consult") or an
 * assessment ("psychiatry_assessment", "therapy_assessment"). This returns true for
 * specifically the therapy_assessment service type so UI copy can be adapted accordingly.
 */
export const isAssessmentAppointmentType = (appointmentType?: string | null) =>
  Boolean(
    appointmentType?.includes("therapy_assessment") ||
      appointmentType?.includes("psychiatry_assessment"),
  );

/**
 * @name getConsultToolLabel
 * @description Returns the service-type-aware label for the consult/assessment tool link.
 * Therapy/psychiatry assessments use "Assessment Tool"; consults stay "Consult Tool".
 * Do not key this off the Milestone 3 flag — Past still includes historical consults.
 */
export const getConsultToolLabel = (appointmentType?: string | null) =>
  isAssessmentAppointmentType(appointmentType)
    ? "Assessment Tool"
    : "Consult Tool";

/**
 * @name getConsultNoteLabel
 * @description Returns the service-type-aware label for the consult/assessment note link.
 * Therapy/psychiatry assessments use "Assessment Note"; consults stay "Consult Note".
 * Do not key this off the Milestone 3 flag — Past still includes historical consults.
 */
export const getConsultNoteLabel = (appointmentType?: string | null) =>
  isAssessmentAppointmentType(appointmentType)
    ? "Assessment Note"
    : "Consult Note";

export const getMyConsultsPageTitle = (isPathwaysMilestone3 = false) =>
  isPathwaysMilestone3 ? "My Assessments" : "My Consults";

export const fuzzyMatch = (
  searchTerm: string,
  fullName: string | null,
  clientId: string | undefined,
) => {
  if (!fullName) return false;
  const normalizedSearchTerm = searchTerm.toLowerCase();
  const normalizedFullName = fullName.toLowerCase();

  return (
    normalizedFullName.includes(normalizedSearchTerm) ||
    clientId?.includes(normalizedSearchTerm)
  );
};

export const isMatchDelayed = (timestamp: string | null) => {
  if (timestamp !== null) {
    const matchAfter = DateTime.fromISO(timestamp);
    const currentDateTime = DateTime.now();
    return matchAfter > currentDateTime;
  } else {
    return false;
  }
};

export const formatPastData = (data: MyMatch[]) =>
  data.map((datum) => ({
    match: datum,
    consult: {
      consultId: datum?.consult_id,
      healthieNoteId: datum?.consult_event?.healthie_note_id,
      healthieId: datum?.ehr_client?.healthie_id,
      client: datum?.ehr_client,
      clinician: datum?.consult_event?.clinician?.id,
      match: {
        ...datum,
        client: {
          ...datum?.ehr_client,
          payers: datum?.ehr_client?.client_payer_service_records,
        },
      },
      serviceType: datum?.consult_event?.service_type,
    },
    client: {
      fullName: `${datum?.ehr_client?.first_name} ${datum?.ehr_client?.last_name}`,
      clientId: datum?.ehr_client?.id,
      payerAcronym:
        datum?.ehr_client?.client_payer_service_records?.[0]?.payer?.acronym,
      payerDisplayName:
        datum?.ehr_client?.client_payer_service_records?.[0]?.payer
          ?.display_name,
      isFormerClient: datum?.former_client,
      isHighPriority: datum?.priority === "high_acuity",
      isRematch: datum?.is_rematch,
      email: datum?.ehr_client?.email,
    },
    startTime: datum?.consult_event?.start_time,
    statusOrStartTime: {
      status: isMatchDelayed(datum?.match_after)
        ? "Delayed"
        : datum?.client_status,
      startTime: datum?.consult_event?.start_time || datum?.created_at,
      hasConsult: datum?.consult_event ? true : false,
    },
    serviceType: datum?.service_type,
  }));

// NOTE: this is less than ideal
export type FormattedPastMatch = ReturnType<typeof formatPastData>[number];

/**
 * @name formatPastConsultData
 * @description Shapes an event-backed past session into the same row the Past tab
 * renders for match-backed sessions. These rows have no match, so match-derived
 * fields (status, former client, rematch) are absent rather than wrong.
 */
export const formatPastConsultData = (data: PastConsult[]) =>
  data.map((datum) => ({
    match: null,
    consult: {
      consultId: datum?.id,
      matchId: datum?.ehr_matches?.[0]?.id ?? null,
      healthieNoteId: datum?.healthie_note_id,
      healthieId: datum?.client?.healthie_id,
      client: datum?.client,
      clinician: datum?.clinician_id,
      match: null,
      serviceType: datum?.service_type,
      startTime: datum?.start_time,
      timeScheduled: datum?.time_scheduled,
    },
    client: {
      fullName: `${datum?.client?.first_name} ${datum?.client?.last_name}`,
      clientId: datum?.client?.id,
      payerAcronym:
        datum?.client?.client_payer_service_records?.[0]?.payer?.acronym,
      payerDisplayName:
        datum?.client?.client_payer_service_records?.[0]?.payer?.display_name,
      isFormerClient: false,
      isHighPriority: false,
      isRematch: false,
      email: datum?.client?.email,
      needsInterpretation:
        datum?.client?.ehr_referrals?.[0]?.needs_interpreter_services,
    },
    startTime: datum?.start_time,
    statusOrStartTime: {
      status: null,
      startTime: datum?.start_time,
      hasConsult: true,
    },
    serviceType: datum?.service_type,
  }));

// NOTE: this is less than ideal
export type FormattedPastConsult = ReturnType<
  typeof formatPastConsultData
>[number];

export type FormattedPastRow = FormattedPastMatch | FormattedPastConsult;

/**
 * @name mergePastRows
 * @description Unions the match-backed and event-backed Past rows, dropping event
 * rows whose session is already represented by a match, then orders by session date.
 */
export const mergePastRows = (
  matchRows: FormattedPastMatch[],
  consultRows: FormattedPastConsult[],
): FormattedPastRow[] => {
  const consultIdsFromMatches = new Set(
    matchRows
      .map((row) => row.consult?.consultId)
      .filter(Boolean)
      .map(String),
  );

  const rows: FormattedPastRow[] = [
    ...matchRows,
    ...consultRows.filter(
      (row) => !consultIdsFromMatches.has(String(row.consult.consultId)),
    ),
  ];

  const startTimeMillis = (row: FormattedPastRow) => {
    const millis = DateTime.fromISO(
      row.statusOrStartTime?.startTime ?? "",
    ).toMillis();
    return Number.isNaN(millis) ? 0 : millis;
  };

  return rows.sort((a, b) => startTimeMillis(b) - startTimeMillis(a));
};

type PastFilterOption = { label: string; value: boolean };

/**
 * @name filterPastConsults
 * @description Applies the Past tab's search and date filters to event-backed rows.
 * Client status only exists on matches, so an active status filter excludes these rows.
 */
export const filterPastConsults = (
  consults: PastConsult[],
  {
    searchTerm,
    selectedClientId,
    filterDropdowns,
  }: {
    searchTerm: string;
    selectedClientId?: string;
    filterDropdowns: {
      client_status: PastFilterOption[];
      month: PastFilterOption[];
      year: PastFilterOption[];
    };
  },
) => {
  if (filterDropdowns.client_status?.some((status) => status.value)) {
    return [];
  }

  const anyMonthTrue = filterDropdowns.month.some((month) => month.value);
  const anyYearTrue = filterDropdowns.year.some((year) => year.value);

  return consults.filter((consult) => {
    if (
      selectedClientId &&
      String(consult.client?.id) !== String(selectedClientId)
    ) {
      return false;
    }

    const nameMatches = fuzzyMatch(
      searchTerm,
      `${consult.client?.first_name} ${consult.client?.last_name}`,
      consult.client?.id?.toString(),
    );
    if (!nameMatches) {
      return false;
    }

    const date = new Date(consult.start_time);
    const monthOfDate = date.toLocaleString("default", { month: "long" });
    const yearOfDate = String(date.getFullYear());

    const monthMatches =
      !anyMonthTrue ||
      filterDropdowns.month.some(
        (month) => month.label === monthOfDate && month.value,
      );
    const yearMatches =
      !anyYearTrue ||
      filterDropdowns.year.some(
        (year) => year.label === yearOfDate && year.value,
      );

    return monthMatches && yearMatches;
  });
};

export const formatUpcomingAndIncompleteData = (data: RecentConsult[]) =>
  data.map((datum) => ({
    consult: {
      id: datum.id,
      consultId: datum.id,
      healthieNoteId: datum?.healthie_note_id,
      serviceType: datum?.service_type,
      ehrMatches: datum?.ehr_matches,
      healthieId: datum?.client?.healthie_id,
      clinician: datum?.clinician_id,
      startTime: datum?.start_time,
      timeScheduled: datum?.time_scheduled,
      client: {
        ...datum?.client,
        payers: datum?.client?.client_payer_service_records,
      },
    },
    client: {
      fullName: `${datum?.client?.first_name} ${datum?.client?.last_name}`,
      id: datum?.client?.id,
      needsInterpretation:
        datum?.client?.ehr_referrals[0]?.needs_interpreter_services,
    },
    startTime: datum.start_time,
    statusOrStartTime: {
      status: null,
      startTime: datum?.start_time,
    },
  }));

// NOTE: this is less than ideal
export type FormattedRecentConsult = ReturnType<
  typeof formatUpcomingAndIncompleteData
>[number];

export const customSort = (a: any, b: any) => {
  const startTimeA = a.consult_event
    ? new Date(a.consult_event?.start_time).getTime()
    : new Date(a.created_at).getTime();
  const startTimeB = b.consult_event
    ? new Date(b.consult_event?.start_time).getTime()
    : new Date(b.created_at).getTime();

  return (
    startTimeB - startTimeA ||
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
};

export const checkForAndAppendMedicareAndMedical = (matchData: Array<any>) => {
  matchData.forEach((match) => {
    if (
      match.ehr_client.ehr_insurancerecords &&
      match.ehr_client.ehr_insurancerecords.length
    ) {
      const orderedRecords = match.ehr_client.ehr_insurancerecords
        ?.slice()
        .sort((a: any, b: any) => {
          if (
            !a?.effective_termination_date &&
            !b?.effective_termination_date
          ) {
            return 0;
          } else if (!a?.effective_termination_date) {
            return -1;
          } else if (!b?.effective_termination_date) {
            return 1;
          }

          return (
            DateTime.fromISO(b.effective_termination_date).toMillis() -
            DateTime.fromISO(a.effective_termination_date).toMillis()
          );
        });
      match.is_medicare = orderedRecords[0].is_medicare;
      match.is_medical = orderedRecords[0].is_medical;
    }
  });
  return matchData;
};

type ClientEventForConsultBackfill = {
  id: string;
  start_time?: string | null;
  healthie_note_id?: string | null;
  service_type?: string | null;
  clinician?: { id: number } | null;
  ehr_consulttools?: Array<{
    id: string;
    event_id?: string | null;
    created_at?: string | null;
  }> | null;
};

/**
 * BANDAID: When match.consult_id is null, recover the consult event by taking
 * the earliest ehr_consulttool for that client with created_at > match.created_at
 * (consulttools are created after the match). Falls back to the latest client
 * event with start_time <= match.created_at.
 */
export const backfillMissingConsultIds = (matchData: Array<any>) => {
  matchData.forEach((match) => {
    if (match.consult_id) {
      return;
    }

    const events = (match.ehr_client?.events ??
      []) as ClientEventForConsultBackfill[];
    if (!events.length) {
      return;
    }

    const matchCreatedMs = match.created_at
      ? DateTime.fromISO(match.created_at).toMillis()
      : Number.NEGATIVE_INFINITY;

    let bestToolEvent: ClientEventForConsultBackfill | null = null;
    let bestToolCreatedMs = Number.POSITIVE_INFINITY;

    for (const event of events) {
      for (const tool of event.ehr_consulttools ?? []) {
        if (!tool.created_at) continue;
        const toolCreatedMs = DateTime.fromISO(tool.created_at).toMillis();
        if (
          toolCreatedMs > matchCreatedMs &&
          toolCreatedMs < bestToolCreatedMs
        ) {
          bestToolCreatedMs = toolCreatedMs;
          bestToolEvent = event;
        }
      }
    }

    let resolvedEvent = bestToolEvent;

    if (!resolvedEvent) {
      let bestEventStartMs = Number.NEGATIVE_INFINITY;
      for (const event of events) {
        if (!event.start_time) continue;
        const startMs = DateTime.fromISO(event.start_time).toMillis();
        if (startMs <= matchCreatedMs && startMs > bestEventStartMs) {
          bestEventStartMs = startMs;
          resolvedEvent = event;
        }
      }
    }

    if (!resolvedEvent) {
      return;
    }

    match.consult_id = resolvedEvent.id;
    match.consult_event = {
      start_time: resolvedEvent.start_time,
      healthie_note_id: resolvedEvent.healthie_note_id,
      clinician: resolvedEvent.clinician,
      service_type: resolvedEvent.service_type,
    };
  });
  return matchData;
};

/**
 * @name getRelevantConsultClinicianMatchEvents
 * @description This function is used to get the relevant match events for a clinician. When someone is the originating consult clinician,
 * they should have the ability to see all of the events for a client.
 * @param allClientMatchEvents
 */
export const getRelevantConsultClinicianMatchEvents = (
  allClientMatchEvents: Array<any>,
  clinicianId: number,
): Array<any> => {
  const sortedEvents = allClientMatchEvents.sort(customSort);
  const isOriginatingConsultClinician =
    sortedEvents.length &&
    sortedEvents[sortedEvents.length - 1].assignee_auth_user.ehr_clinician
      .id === clinicianId;
  return isOriginatingConsultClinician
    ? sortedEvents
    : sortedEvents.filter(
        (event) => event.assignee_auth_user.ehr_clinician.id === clinicianId,
      );
};

/**
 * @name getClientsMapFromPastConsultsData
 * @description Gets the clients from the past consults data as a map of client id to client
 */
export const getClientsMapFromPastConsultsData = (
  allClientMatchEvents: Array<any>,
): Map<string, any> => {
  let clientMap = new Map<string, any>();

  allClientMatchEvents.forEach((event) => {
    clientMap.set(event.client.clientId, event.client);
  });
  return clientMap;
};
